// Charts, drawn on demand (B-406): the charting library is loaded the first
// time a screen draws a chart, so a screen without one never pays for it.
// Until it arrives, the chart's own space, empty, so nothing below it jumps.

import { lazy, Suspense } from 'react';
import type { WeekPoints } from './weeksChart';

const WeeksChart = lazy(() => import('./weeksChart'));

export type { WeekPoints };

/**
 * A player's last games as a small line, oldest on the left, the last a dot
 * (B-410). Drawn here and not by the charting library: a list of a hundred
 * rows cannot load it for each. A picture of numbers said in words beside it,
 * so a screen reader is not read it. From zero, so a line of 20s does not
 * look like a line of 2s; below zero when a game went there. Nothing under
 * two games, where there is no line to draw.
 */
export function FormLine({
  points,
  width = 48,
  height = 18,
}: {
  points: readonly number[];
  width?: number;
  height?: number;
}) {
  if (points.length < 2) return null;
  const low = Math.min(0, ...points);
  const high = Math.max(low + 1, ...points);
  const x = (i: number) => 2 + (i * (width - 4)) / (points.length - 1);
  const y = (v: number) => height - 2 - ((v - low) / (high - low)) * (height - 4);
  const path = points
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`)
    .join(' ');
  const last = points.length - 1;
  return (
    <svg
      className="fsp-formline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
    >
      <path d={path} />
      <circle cx={x(last)} cy={y(points[last]!)} r={2} />
    </svg>
  );
}

/**
 * Points week by week, oldest on the left. A picture of the table it sits
 * over, which holds every number, so a screen reader is not read it twice.
 */
export function PointsByWeek({ weeks, average }: { weeks: WeekPoints[]; average: number | null }) {
  return (
    <div className="fsp-chart" aria-hidden>
      <Suspense fallback={<div className="fsp-chart-wait" />}>
        <WeeksChart weeks={weeks} average={average} />
      </Suspense>
    </div>
  );
}
